import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { StateService } from '../../services/state';
import { Api } from '../../services/api';
import { spanishDniNieValidator } from '../../validators/spanish-dni-nie.validator';
import { postalEspanolSenseCeutaMelillaValidator } from '../../validators/spanish-postal-code.validator';
import { minAge18Validator } from '../../validators/min-age.validator';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { HttpHeaders } from '@angular/common/http';
import { Notifications } from '../../services/notifications';
import { UtilsService } from '../../services/utils';

@Component({
  selector: 'app-contract-form',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCardModule,
    MatIconModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatGridListModule,
  ],
  templateUrl: './contract-form.html',
  styleUrl: './contract-form.scss',
})
export class ContractForm {
  contractForm!: FormGroup;
  state: any;

  @ViewChild('emailInput') emailInput?: ElementRef<HTMLInputElement>;

  private readonly api = inject(Api);
  private readonly router = inject(Router);
  private readonly stateService = inject(StateService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly notification = inject(Notifications);
  private readonly utils = inject(UtilsService);

  ngOnInit() {
    this.state = this.stateService.getState();
    if (
      !this.state ||
      !this.state.idVersionDivina ||
      !this.state.duration ||
      !this.state.checkAvisoLegal
    ) {
      this.router.navigate(['/']);
    }

    this.contractForm = this.formBuilder.group({
      // PERSONES
      tomador: this.buildExtendedPersonGroup(),
      conductor: this.buildPersonGroup('conductor'),
      propietari: this.buildPersonGroup('propietari'),

      // “toggles” per dir si són la mateixa persona
      sameDriverAsTomador: [this.state?.sameDriverAsTomador ?? true],
      sameOwnerAsTomador: [this.state?.sameOwnerAsTomador ?? true],

      // Verificació matrícula
      plateVerification: ['', Validators.required],

      // Confirmació final de dades
      allCorrect: [false, Validators.requiredTrue],
    });

    this.contractForm.get('sameDriverAsTomador')!.valueChanges.subscribe((isSame) => {
      const conductorGroup = this.contractForm.get('conductor') as FormGroup;
      this.toggleRequiredValidators(conductorGroup, !isSame);
      this.updateEuLicenseValidator('conductor');
    });

    this.contractForm.get('sameOwnerAsTomador')!.valueChanges.subscribe((isSame) => {
      const propietariGroup = this.contractForm.get('propietari') as FormGroup;
      this.toggleRequiredValidators(propietariGroup, !isSame);
      this.updateEuLicenseValidator('propietari');
    });

    // Subscripció als canvis de DNI per detectar NIE i actualitzar validació euLicense
    ['tomador', 'conductor', 'propietari'].forEach((role) => {
      this.contractForm.get(`${role}.dni`)?.valueChanges.subscribe(() => {
        this.updateEuLicenseValidator(role);
      });
    });

    // Inicialitzar correctament segons valors inicials
    this.toggleRequiredValidators(
      this.contractForm.get('conductor') as FormGroup,
      !this.contractForm.get('sameDriverAsTomador')!.value
    );
    this.toggleRequiredValidators(
      this.contractForm.get('propietari') as FormGroup,
      !this.contractForm.get('sameOwnerAsTomador')!.value
    );

    // Inicialitzar validators euLicense (en cas de que hi hagi un NIE ja omplert)
    ['tomador', 'conductor', 'propietari'].forEach((role) => {
      this.updateEuLicenseValidator(role);
    });
  }

  get plateVerificationCtrl() {
    return this.contractForm.get('plateVerification')!;
  }

  get platesMatch(): boolean {
    const normalize = (v: string) => (v || '').toUpperCase().replace(/[\s\-]/g, '');
    return normalize(this.state?.licensePlate) === normalize(this.plateVerificationCtrl.value);
  }

  goToDates() {
    // Desa el que s'ha omplert perquè no es perdi en tornar
    const { tomador, conductor, propietari, sameDriverAsTomador, sameOwnerAsTomador } =
      this.contractForm.value;
    this.stateService.setState({
      ...this.stateService.getState(),
      tomador,
      conductor,
      propietari,
      sameDriverAsTomador,
      sameOwnerAsTomador,
    });
    this.router.navigate(['/price-result']);
  }

  goToEmail() {
    const input = this.emailInput?.nativeElement;
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus({ preventScroll: true });
  }

  isNie(value: string): boolean {
    return /^[XYZ]/i.test(value || '');
  }

  buildExtendedPersonGroup(): FormGroup {
    return this.formBuilder.group({
      name: [this.state?.tomador?.name || '', Validators.required],
      surname1: [this.state?.tomador?.surname1 || '', Validators.required],
      surname2: [this.state?.tomador?.surname2 || ''],
      birthday: [this.state?.tomador?.birthday || '', [Validators.required, minAge18Validator()]],
      dni: [this.state?.tomador?.dni || '', [Validators.required, spanishDniNieValidator()]],
      euLicense: [this.state?.tomador?.euLicense ?? false],
      postalCode: [
        this.state?.tomador?.postalCode || '',
        [Validators.required, postalEspanolSenseCeutaMelillaValidator()],
      ],
      address: [this.state?.tomador?.address || '', Validators.required],
      city: [this.state?.tomador?.city || '', Validators.required],
      email: [this.state?.tomador?.email || '', Validators.required],
      phone: [this.state?.tomador?.phone || '', Validators.required],
      licenseDate: [this.state?.tomador?.licenseDate || '', [Validators.required]],
    });
  }

  buildPersonGroup(dataOrigin: string): FormGroup {
    return this.formBuilder.group({
      name: [this.state?.[dataOrigin]?.name || '', Validators.required],
      surname1: [this.state?.[dataOrigin]?.surname1 || '', Validators.required],
      surname2: [this.state?.[dataOrigin]?.surname2 || ''],
      birthday: [
        this.state?.[dataOrigin]?.birthday || '',
        [Validators.required, minAge18Validator()],
      ],
      dni: [this.state?.[dataOrigin]?.dni || '', [Validators.required, spanishDniNieValidator()]],
      euLicense: [this.state?.[dataOrigin]?.euLicense ?? false],
      licenseDate: [this.state?.[dataOrigin]?.licenseDate || '', [Validators.required]],
      /*postalCode: ['', [Validators.required, postalEspanolSenseCeutaMelillaValidator()]],
      address: ['', Validators.required],
      email: ['', Validators.required],
      phone: ['', Validators.required],*/
    });
  }

  private updateEuLicenseValidator(role: string) {
    const dniValue = this.contractForm.get(`${role}.dni`)?.value || '';
    const euLicenseControl = this.contractForm.get(`${role}.euLicense`);
    if (!euLicenseControl) return;

    const isGroupActive =
      role === 'tomador' ||
      (role === 'conductor' && !this.contractForm.get('sameDriverAsTomador')?.value) ||
      (role === 'propietari' && !this.contractForm.get('sameOwnerAsTomador')?.value);

    if (isGroupActive && this.isNie(dniValue)) {
      euLicenseControl.setValidators([Validators.requiredTrue]);
    } else {
      euLicenseControl.clearValidators();
    }
    euLicenseControl.updateValueAndValidity();
  }

  private toggleRequiredValidators(group: FormGroup, isRequired: boolean) {
    Object.keys(group.controls).forEach((key) => {
      if (key === 'euLicense') return; // gestionat per updateEuLicenseValidator

      const control = group.get(key)!;
      const validators = [];

      if (isRequired) {
        // torna a posar els validators originals
        if (key === 'birthday') {
          validators.push(Validators.required, minAge18Validator());
        } else if (key === 'dni') {
          validators.push(Validators.required, spanishDniNieValidator());
        } else {
          validators.push(Validators.required);
        }
      }

      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }

  onSubmit() {
    if (this.contractForm.valid) {
      const state = this.stateService.getState();
      if (!state) {
        this.router.navigate(['/']);
        return;
      }

      const formValue = this.contractForm.value;

      // Si el conductor és el mateix que el tomador, copia les dades
      if (formValue.sameDriverAsTomador) {
        formValue.conductor = { ...formValue.tomador };
      }

      // Si el propietari és el mateix que el tomador, copia les dades
      if (formValue.sameOwnerAsTomador) {
        formValue.propietari = { ...formValue.tomador };
      }

      formValue.tomador.dni = formValue.tomador.dni.toUpperCase();
      formValue.conductor.dni = formValue.conductor.dni.toUpperCase();
      formValue.propietari.dni = formValue.propietari.dni.toUpperCase();

      const normDate = (d: any) => this.utils.toIsoAtUtcMidnightKeepDay(d);
      const normPerson = (p: any) => p ? {
        ...p,
        birthday: normDate(p.birthday),
        licenseDate: normDate(p.licenseDate),
      } : p;

      const contractData = {
        ...state,
        ...formValue,
        start_date: normDate(state.start_date),
        end_date: normDate(state.end_date),
        tomador: normPerson(formValue.tomador),
        conductor: normPerson(formValue.conductor),
        propietari: normPerson(formValue.propietari),
      };

      this.stateService.setState(contractData);

      this.api
        .post('/seguro-dias/opportunity', contractData, {
          headers: new HttpHeaders().set('Content-Type', 'application/json'),
        })
        .subscribe(
          (data: any) => {
            this.stateService.setState(Object.assign(this.stateService.getState(), { ...data }));
            this.router.navigate(['/contract-preview']);
          },
          (error) => {
            //TODO notify error
            this.notification.showError(
              'Ha ocurrido un error al enviar el formulario. Por favor, inténtalo de nuevo más tarde.'
            );
            this.router.navigate(['/contract-preview']);
          }
        );
    }
  }
}
